import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  SegControl,
  Table,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableBodyCell,
  NumberInput,
} from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { round } from 'vend-number'
import { Currency } from '@vend/business-components'
import { formatCurrencyValue, getCurrencyFormatParts } from '@vend/utilities'
import { getCurrencyDenominations } from '../../../constants/currency-denomination'

import {
  useRegisterManager,
  useRetailerSettingsManager,
  useVdFeatureManager,
} from '../../../ng-compat/hooks'
import { LRX_28094_COLLAPSIBLE_CASH_COUNTING } from '../../../constants'
import { DenominationQuantity } from '../../../util/register-closure-types'
import { DECIMAL_PLACES } from '../useFormatClosureNumber'
import { RegisterClosureCashCountingTotal } from './RegisterClosureCashCountingTotal'

enum COUNTING_MODE {
  TOTAL_ONLY = 'total_only',
  BY_DENOMINATION = 'by_denomination',
}

interface RegisterClosureCashCountingProps {
  currencyCode: string
  value: string
  paymentName: string
  onChange: (total: string) => void
  onDenominationChanged?: (denominations: DenominationQuantity[]) => void
  /** When this changes (e.g. register session), denomination / draft totals reset so values are not carried across sessions. */
  closureSessionKey?: string
}

export const RegisterClosureCashCounting: React.FC<RegisterClosureCashCountingProps> = ({
  currencyCode,
  paymentName,
  value,
  onChange,
  onDenominationChanged,
  closureSessionKey,
}) => {
  const currentOutlet = useRegisterManager().getActiveOutlet()
  const retailer = useRetailerSettingsManager().get()
  const hasCollapsibleCashCounting = useVdFeatureManager().get(
    LRX_28094_COLLAPSIBLE_CASH_COUNTING
  )

  const denominations: number[] = useMemo(() => {
    return getCurrencyDenominations(currencyCode)
  }, [currencyCode])

  const decimalSeparator = useMemo(
    () =>
      getCurrencyFormatParts(
        retailer?.culture || '',
        currentOutlet.currency,
        currentOutlet.currency_symbol
      ).decimal,
    [retailer?.culture, currentOutlet.currency, currentOutlet.currency_symbol]
  )

  const [countingMode, setCountingMode] = useState<COUNTING_MODE>(
    COUNTING_MODE.BY_DENOMINATION
  )
  const [quantities, setQuantities] = useState<number[]>(() =>
    denominations.map(() => 0)
  )
  const [customAmount, setCustomAmount] = useState<string>('')
  const [directTotal, setDirectTotal] = useState<string>(value || '0.00')

  const [total, setTotal] = useState<string>(value || '0.00')

  const prevSessionRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    setQuantities(prev => {
      if (prev.length === denominations.length) {
        return prev
      }
      return denominations.map((_, i) => prev[i] ?? 0)
    })
  }, [denominations])

  useEffect(() => {
    if (!closureSessionKey) {
      return
    }

    const prev = prevSessionRef.current
    if (prev !== undefined && prev !== closureSessionKey) {
      setQuantities(denominations.map(() => 0))
      setCustomAmount('')
      setDirectTotal(value || '0.00')
      setCountingMode(COUNTING_MODE.BY_DENOMINATION)
    }
    prevSessionRef.current = closureSessionKey
  }, [closureSessionKey, denominations, value])

  useEffect(() => {
    if (!retailer) {
      return
    }

    if (
      hasCollapsibleCashCounting &&
      countingMode === COUNTING_MODE.TOTAL_ONLY
    ) {
      const cleaned = directTotal === '' ? '0' : directTotal
      const rounded = round(cleaned, DECIMAL_PLACES)
      setTotal(rounded)
      onChange(rounded)
      onDenominationChanged?.([])
      return
    }

    const denominationsTotal = quantities.reduce(
      (sum, qty, i) => sum + qty * denominations[i],
      0
    )
    const customAmountValue = parseFloat(customAmount) || 0
    const newTotal = denominationsTotal + customAmountValue
    const rounded = round(newTotal, DECIMAL_PLACES)
    const denominationMap = denominations.map((denomination, index) => {
      return {
        denomination,
        quantity: quantities[index] ?? 0,
      }
    })
    setTotal(rounded)
    onChange(rounded)
    if (onDenominationChanged) {
      onDenominationChanged(denominationMap)
    }
    // retailer + callbacks are excluded to avoid cascading when parent rerenders redefine handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- propagate only from counting inputs / currency row
  }, [
    countingMode,
    directTotal,
    quantities,
    customAmount,
    denominations,
    hasCollapsibleCashCounting,
  ])

  const handleQuantityChange = (index: number, qty: number) => {
    setQuantities(quantities => {
      const updatedQuantities = [...quantities]
      updatedQuantities[index] = qty
      return updatedQuantities
    })
  }

  const handleCustomAmountChange = (val: number) => {
    setCustomAmount(val.toString())
  }

  const handleDirectTotalChange = (val: string) => {
    setDirectTotal(val)
  }

  if (!retailer) {
    return null
  }

  return (
    <div className="wr-register-closure-cash-counting">
      {hasCollapsibleCashCounting && (
        <div className="vd-flex">
          <SegControl
            checked={countingMode === COUNTING_MODE.TOTAL_ONLY}
            className="vd-segcontrol--stretched"
            name="registerClosureCashCountingMode"
            onChange={() => {
              setCountingMode(COUNTING_MODE.TOTAL_ONLY)
            }}
            role="radio"
            data-track="register-closure-cash-total-only"
          >
            <T
              _str="Enter cash total only"
              _context="Seg control label for entering cash total only during register closure"
              _key="webregister.register-closure.cash-counting.mode.total-only"
            />
          </SegControl>
          <SegControl
            checked={countingMode === COUNTING_MODE.BY_DENOMINATION}
            className="vd-segcontrol--stretched"
            data-testid="count-by-denomination-mode-btn"
            name="registerClosureCashCountingMode"
            onChange={() => setCountingMode(COUNTING_MODE.BY_DENOMINATION)}
            role="radio"
            data-track="register-closure-cash-by-denomination"
          >
            <T
              _str="Count cash by denomination"
              _context="Seg control label for counting cash by denomination during register closure"
              _key="webregister.register-closure.cash-counting.mode.by-denomination"
            />
          </SegControl>
        </div>
      )}

      {countingMode === COUNTING_MODE.BY_DENOMINATION && (
        <Table className="wr-closure-cash-counting vd-mb10">
          <TableHead>
            <TableRow>
              <TableHeadCell className="vd-align-right">
                <T
                  _str="Denomination"
                  _context="Table column header for denomination in register closure cash counting"
                  _key="webregister.register-closure.cash-counting.table.denomination"
                />
              </TableHeadCell>
              <TableHeadCell className="vd-align-right">
                <T
                  _str="Quantity"
                  _context="Table column header for quantity in register closure cash counting"
                  _key="webregister.register-closure.cash-counting.table.quantity"
                />
              </TableHeadCell>
              <TableHeadCell className="vd-align-right">
                <T
                  _str="Amount"
                  _context="Table column header for amount in register closure cash counting"
                  _key="webregister.register-closure.cash-counting.table.amount"
                />
              </TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {denominations.map((denom: number, index: number) => (
              <TableRow key={`denomination-row-${index}`}>
                <TableBodyCell className="vd-align-right">
                  {formatCurrencyValue(
                    denom,
                    retailer.culture,
                    currentOutlet.currency,
                    currentOutlet.currency_symbol
                  )}
                </TableBodyCell>
                <TableBodyCell
                  data-testid="denomination-input-parent"
                  className="vd-align-right wr-denomination-input"
                >
                  <NumberInput
                    min={0}
                    max={1000000}
                    value={quantities[index] ?? 0}
                    data-testid={`denomination-input-${index}`}
                    onChange={val => handleQuantityChange(index, Number(val))}
                    validators={{
                      nonDecimal: true,
                    }}
                    selectTextOnClick
                  />
                </TableBodyCell>
                <TableBodyCell className="vd-align-right">
                  <Currency value={(quantities[index] * denom).toFixed(2)} />
                </TableBodyCell>
              </TableRow>
            ))}
            <TableRow>
              <TableBodyCell className="vd-align-right">
                <strong>
                  <T
                    _str="Custom Amount"
                    _context="Table row label for custom amount in register closure cash counting"
                    _key="webregister.register-closure.cash-counting.table.custom-amount"
                  />
                </strong>
              </TableBodyCell>
              <TableBodyCell className="vd-align-right">—</TableBodyCell>
              <TableBodyCell className="vd-align-right">
                <NumberInput
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  placeholder="0.00"
                  decimalSeparator={decimalSeparator}
                  data-testid="custom-amount-input"
                />
              </TableBodyCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
      <RegisterClosureCashCountingTotal
        decimalSeparator={decimalSeparator}
        editableValue={directTotal}
        onEditableValueChange={handleDirectTotalChange}
        paymentName={paymentName}
        total={total}
        totalEditable={
          hasCollapsibleCashCounting &&
          countingMode === COUNTING_MODE.TOTAL_ONLY
        }
      />
    </div>
  )
}
